import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Gavel, Clock, Check, X } from "lucide-react";

const STATUS = {
  pending: { label: "In afwachting", cls: "text-amber-400 bg-amber-950/30", icon: Clock },
  accepted: { label: "Geaccepteerd", cls: "text-emerald-400 bg-emerald-950/30", icon: Check },
  declined: { label: "Afgewezen", cls: "text-rose-400 bg-rose-950/30", icon: X },
  countered: { label: "Tegenbod", cls: "text-amber-400 bg-amber-950/30", icon: Gavel },
};

export default function MyBids() {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/my/bids").then(({ data }) => setBids(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const respond = async (bidId, action) => {
    try {
      await api.patch(`/bids/${bidId}/respond?action=${action}`);
      toast.success(action === "accept" ? "Tegenbod geaccepteerd" : "Tegenbod afgewezen");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Actie mislukt");
    }
  };

  const withdraw = async (bidId) => {
    if (!window.confirm("Weet je zeker dat je je bod wilt intrekken?")) return;
    try {
      await api.delete(`/bids/${bidId}`);
      toast.success("Je bod is ingetrokken");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Actie mislukt");
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8">
      <div className="flex items-center gap-2 mb-1">
        <Gavel className="h-4 w-4 text-rose-400" />
        <span className="font-mono text-xs uppercase tracking-widest text-rose-400">Biedingen</span>
      </div>
      <h1 className="font-serif text-4xl font-semibold text-zinc-100 mb-6">Mijn biedingen</h1>

      {loading ? (
        <div className="text-zinc-500">Laden...</div>
      ) : bids.length === 0 ? (
        <div data-testid="no-bids" className="text-center py-24 rounded-xl border border-zinc-800 bg-zinc-900/40">
          <Gavel className="h-10 w-10 text-zinc-700 mx-auto mb-4" />
          <p className="font-serif text-xl text-zinc-300">Je hebt nog geen biedingen gedaan</p>
          <Link to="/" className="inline-block mt-4 text-rose-400 hover:text-rose-300 text-sm">Ontdek advertenties</Link>
        </div>
      ) : (
        <div className="space-y-3">
          {bids.map((b) => {
            const s = STATUS[b.status] || STATUS.pending;
            return (
              <div key={b.id} data-testid={`my-bid-${b.id}`} className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <Link to={`/product/${b.product_id}`} className="text-sm font-medium text-zinc-200 truncate block hover:text-rose-300">{b.product_title}</Link>
                    <div className="text-xs text-zinc-500 mt-0.5">Jouw bod: <span className="font-mono text-rose-300">€ {Number(b.amount).toFixed(2)}</span></div>
                  </div>
                  <span className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium shrink-0 ${s.cls}`}>
                    <s.icon className="h-3.5 w-3.5" /> {s.label}
                  </span>
                </div>
                {b.status === "countered" && (
                  <div className="mt-3 rounded-md bg-amber-950/20 border border-amber-800/40 p-3">
                    <div className="text-xs text-amber-200 mb-2">De verkoper stelt <span className="font-mono font-semibold">€ {Number(b.counter_amount).toFixed(2)}</span> voor.</div>
                    <div className="flex gap-2">
                      <button data-testid={`accept-counter-${b.id}`} onClick={() => respond(b.id, "accept")} className="flex-1 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium px-3 py-2">Accepteer € {Number(b.counter_amount).toFixed(2)}</button>
                      <button data-testid={`decline-counter-${b.id}`} onClick={() => respond(b.id, "decline")} className="rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800 text-xs px-3 py-2">Afwijzen</button>
                    </div>
                  </div>
                )}
                {(b.status === "pending" || b.status === "countered") && (
                  <button
                    data-testid={`withdraw-bid-${b.id}`}
                    onClick={() => withdraw(b.id)}
                    className="mt-3 flex items-center gap-1.5 rounded-md border border-zinc-800 text-zinc-400 hover:text-rose-300 hover:border-rose-900/60 text-xs font-medium px-3 py-1.5 transition-colors"
                  >
                    <X className="h-3.5 w-3.5" /> Bod intrekken
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
